/**
 * @copyright 2025 Cooltrax Pty Ltd. All rights reserved.
 */

import React, { useEffect, useState } from 'react'

import { Link, useParams } from 'react-router-dom'

import { Alert, Button, PageContainer, PageHeader } from '@/components/ui'
import { useApi, useAuth } from '@/hooks'
import { clearAllCaches } from '@/providers/apiService'
import { clearToken } from '@/services/authService'
import logger from '@/utils/logger'

import { LoadingPanel } from './LoadingPanel'
import type { AdminUserDetail } from './types'
import { AccountCard } from './user-detail/AccountCard'
import { DeleteCard } from './user-detail/DeleteCard'
import { GeotabCard } from './user-detail/GeotabCard'
import { PermissionsCard } from './user-detail/PermissionsCard'

const log = logger({ category: 'AdminUserDetailPage' })

const AdminUserDetailPage: React.FC = () => {
  const { api } = useApi()
  const { user: signedInUser, originalUser, hasPermission } = useAuth()
  const { uuid = '' } = useParams<{ uuid: string }>()

  const [user, setUser] = useState<AdminUserDetail | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [isSwitchingUser, setIsSwitchingUser] = useState(false)
  const [impersonateError, setImpersonateError] = useState('')

  // useApi() hands back the module-level apiService singleton, so `api` is referentially
  // stable and this effect only re-runs when the route param changes.
  useEffect(() => {
    if (!uuid) return
    let cancelled = false

    const load = async () => {
      setIsLoading(true)
      try {
        const response = await api.get<AdminUserDetail>({
          path: `/app/admin/users/${encodeURIComponent(uuid)}`,
          origin: 'AdminUserDetailPage#load',
        })
        if (cancelled) return
        if (response.error || !response.data) {
          setError(response.message || 'Could not load this user')
          return
        }
        setError('')
        setUser(response.data)
      } catch (ex) {
        log.error(ex)
        if (!cancelled) setError('Could not load this user')
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [api, uuid])

  // Impersonation is keyed on email — that is what the sudo endpoint stores and matches.
  // Comparing ids would be unreliable: outside impersonation `user.id` comes from the Auth0
  // access-token `sub`, which isn't always the Management API user_id the profile uses.
  const targetEmail = (user?.email ?? '').toLowerCase()
  // While impersonating, the real signed-in person is originalUser.
  const realEmail = (originalUser?.email ?? signedInUser?.email ?? '').toLowerCase()
  const isSelf = !!targetEmail && targetEmail === realEmail
  const isImpersonatingTarget = !!originalUser && (signedInUser?.email ?? '').toLowerCase() === targetEmail
  const showImpersonate = hasPermission('impersonate') && !!targetEmail && !isSelf

  /**
   * Switch to (or back from) this user. The backend derives the real user from the token,
   * so the uuid in the path is only for symmetry with the existing impersonate modal.
   */
  const handleImpersonate = async (stop: boolean) => {
    if (!user) return
    setIsSwitchingUser(true)
    setImpersonateError('')
    try {
      const response = await api.post<{ error?: boolean; message?: string }, { sudo_email: string }>({
        path: `/app/profiles/${encodeURIComponent(signedInUser?.id ?? user.uid)}/sudo`,
        origin: 'AdminUserDetailPage#impersonate',
        body: { sudo_email: stop ? '' : user.email },
      })
      if (response.error) {
        setImpersonateError(response.message || 'Could not switch user')
        return
      }
      // The token carries the impersonation, so it and every cached response have to go.
      clearAllCaches()
      clearToken()
      // Impersonating a customer drops the `staff` permission, which is what gates this very
      // page — so land somewhere they can still use. Stopping restores it, so stay put.
      if (stop) {
        window.location.reload()
      } else {
        window.location.href = '/'
      }
    } catch (ex) {
      log.error(ex)
      setImpersonateError('Could not switch user')
    } finally {
      setIsSwitchingUser(false)
    }
  }

  if (isLoading) {
    return (
      <PageContainer>
        <LoadingPanel label="Loading user…" />
      </PageContainer>
    )
  }

  if (error || !user) {
    return (
      <PageContainer>
        <Link to="/admin/users" className="link link-hover mb-2 inline-block text-sm">
          ← Users
        </Link>
        <Alert message={error || 'User not found'} type="error" />
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <Link to="/admin/users" className="link link-hover mb-2 inline-block text-sm">
        ← Users
      </Link>
      <PageHeader
        title={user.email || user.uid}
        subtitle={user.displayName || undefined}
        actions={
          showImpersonate ? (
            <Button
              handleClick={() => void handleImpersonate(isImpersonatingTarget)}
              isDisabled={isSwitchingUser}
              variation={isImpersonatingTarget ? 'secondary' : 'outline'}
              size="sm"
            >
              {isSwitchingUser ? (
                <>
                  <span className="loading loading-spinner loading-xs" />
                  Switching…
                </>
              ) : isImpersonatingTarget ? (
                'Stop Impersonating'
              ) : (
                'Impersonate'
              )}
            </Button>
          ) : undefined
        }
      />

      {impersonateError && <Alert message={impersonateError} type="error" />}

      {/* Two independent stacks: the Permissions card grows with the insight list and must
          not stretch the cards beside it. */}
      <div className="grid gap-4 lg:grid-cols-2 lg:items-start">
        <div className="flex flex-col gap-4">
          <AccountCard user={user} />
          <PermissionsCard user={user} uuid={uuid} isSelf={isSelf} />
        </div>
        <div className="flex flex-col gap-4">
          <GeotabCard user={user} uuid={uuid} />
          <DeleteCard user={user} uuid={uuid} isSelf={isSelf} />
        </div>
      </div>
    </PageContainer>
  )
}

export default AdminUserDetailPage
